import { HTMLAttributes, Key, ReactNode, Ref, useMemo, useState } from "react";
import {
  Autocomplete,
  AutocompleteChangeDetails,
  AutocompleteProps,
  AutocompleteRenderValueGetItemProps,
  Checkbox,
  CircularProgress,
  createFilterOptions,
  FormHelperTextProps,
  InputBaseComponentProps,
  InputLabelProps,
  InputProps,
  SxProps,
  TextField,
  TextFieldVariants,
  Theme,
} from "@mui/material";

import { formInputsStyles } from "../../Configuration/styles";
import { ChipTag } from "../../Alert/Filter/Chips/ChipTag";
import { GenericObject } from "../../services/Price";
import { useSelectAllOptions } from "../../hooks/useSelectAllOptions";
import { useShiftRangeSelect } from "../../hooks/useShiftRangeSelect";

type MuiAutocompleteProps<
  T,
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
  FreeSolo extends boolean | undefined
> = AutocompleteProps<T, Multiple, DisableClearable, FreeSolo, "div">;

export type SelectAllRowState = {
  label: string;
  selected: boolean;
  indeterminate: boolean;
};

export type CustomAutocompleteProps<
  T,
  Multiple extends boolean | undefined = false,
  DisableClearable extends boolean | undefined = false,
  FreeSolo extends boolean | undefined = false
> = Omit<
  MuiAutocompleteProps<T, Multiple, DisableClearable, FreeSolo>,
  "renderInput" | "classes"
> & {
  // Extra class name on the Autocomplete root (legacy name kept for consumers)
  classes?: string;
  // The shared form input look (`formInputsStyles.select`); turn it off in filter bars
  formStyle?: boolean;
  label?: ReactNode;
  placeholder?: string;
  error?: boolean;
  helperText?: ReactNode;
  required?: boolean;
  variant?: TextFieldVariants;
  startAdornment?: ReactNode;
  endAdornment?: ReactNode;
  textFieldSx?: SxProps<Theme>;
  // Merged slot by slot on top of the Autocomplete input params; adornments go
  // through `startAdornment` / `endAdornment` so the built-in ones are kept.
  textFieldSlotProps?: {
    formHelperText?: Partial<FormHelperTextProps>;
    htmlInput?: InputBaseComponentProps;
    input?: Partial<InputProps>;
    inputLabel?: Partial<InputLabelProps>;
  };
  // Escape hatch for inputs that are not a TextField; all TextField props above are ignored
  renderInput?: MuiAutocompleteProps<
    T,
    Multiple,
    DisableClearable,
    FreeSolo
  >["renderInput"];
  // Legacy alias of `renderOption`
  renderOptions?: MuiAutocompleteProps<
    T,
    Multiple,
    DisableClearable,
    FreeSolo
  >["renderOption"];
  rootRef?: Ref<HTMLDivElement>;
  // Multiple mode (on by default): adds a "select all" row, and a "select the
  // filtered results" row while a search text is typed. `onChange` only ever
  // receives real options. The search text survives (de)selections. Turn it
  // off on a field that carries its own "all" semantics.
  selectAll?: boolean;
  // With `selectAll`: rows shown in the list (the selection still covers every match)
  maxDisplayedOptions?: number;
  // With `selectAll`: the field's selection cap, "select all" rows are not
  // offered when the candidates would exceed it
  maxSelected?: number;
  // Multiple mode: shift+click adds every option between the last clicked one
  // and the clicked one (never the "select all" rows nor disabled options)
  shiftRangeSelect?: boolean;
  // Options a shift+click range must skip, on top of `getOptionDisabled`
  rangeSelectable?: (option: T) => boolean;
  // Custom look for the "select all" rows (default: small checkbox + label)
  renderSelectAllOption?: (
    props: HTMLAttributes<HTMLLIElement> & { key: Key },
    state: SelectAllRowState
  ) => ReactNode;
  // Multiple mode: option hidden from the chips (ex: -1)
  selectAllOptionId?: number | string;
  // Multiple mode: key displayed in the chips (ex: "label")
  tagsLabelKey?: string | null;
  translateTags?: boolean;
};

export const CustomAutocomplete = <
  T,
  Multiple extends boolean | undefined = false,
  DisableClearable extends boolean | undefined = false,
  FreeSolo extends boolean | undefined = false
>({
  classes,
  className,
  defaultValue,
  endAdornment,
  error,
  filterOptions,
  formStyle = true,
  getOptionDisabled,
  getOptionKey,
  getOptionLabel,
  groupBy,
  helperText,
  inputValue: inputValueProp,
  isOptionEqualToValue,
  label,
  limitTags = 3,
  loading,
  maxDisplayedOptions,
  maxSelected,
  multiple,
  onChange,
  onInputChange,
  options,
  placeholder,
  rangeSelectable,
  renderInput,
  renderSelectAllOption,
  renderOption,
  renderOptions,
  renderValue,
  required,
  rootRef,
  selectAll = true,
  selectAllOptionId,
  shiftRangeSelect = true,
  size = "medium",
  slotProps,
  startAdornment,
  tagsLabelKey,
  textFieldSlotProps,
  textFieldSx,
  translateTags = false,
  value,
  variant = "outlined",
  ...autocompleteProps
}: CustomAutocompleteProps<T, Multiple, DisableClearable, FreeSolo>) => {
  const inputStyles = formInputsStyles();
  const selectAllEnabled = selectAll && !!multiple;
  const shiftRangeEnabled = shiftRangeSelect && !!multiple;

  // MUI resets the search text after each (de)selection; the select-all rows
  // need it to survive, so the input is controlled here unless the consumer does it
  const [innerInputValue, setInnerInputValue] = useState("");
  const inputValue = inputValueProp ?? innerInputValue;

  const resolvedGetOptionLabel: NonNullable<
    MuiAutocompleteProps<
      T,
      Multiple,
      DisableClearable,
      FreeSolo
    >["getOptionLabel"]
  > = useMemo(
    () => getOptionLabel ?? ((option) => String(option)),
    [getOptionLabel]
  );

  // Ids are compared as strings (a URL restore yields "5" for 5). Options
  // without an `id` are told apart by reference: a string conversion would
  // collapse every object into "[object Object]"
  const resolvedGetOptionKey = useMemo(
    () =>
      getOptionKey ??
      ((option: T): unknown => {
        const id = (option as { id?: string | number }).id;
        if (id !== undefined && id !== null) {
          return String(id);
        }
        return typeof option === "object" && option !== null
          ? option
          : String(option);
      }),
    [getOptionKey]
  );

  const selectedValues = useMemo(
    () => (Array.isArray(value) ? (value as T[]) : []),
    [value]
  );

  const selectAllState = useSelectAllOptions<T>({
    enabled: selectAllEnabled,
    options,
    selected: selectedValues,
    inputValue,
    loading,
    getOptionKey: resolvedGetOptionKey as (option: T) => unknown,
    getOptionLabel: resolvedGetOptionLabel as unknown as (option: T) => string,
    filterOptions,
    maxDisplayedOptions,
    maxSelected,
  });

  const effectiveGetOptionLabel: typeof resolvedGetOptionLabel =
    selectAllEnabled
      ? (option) =>
          selectAllState.markerLabel(option as T) ??
          resolvedGetOptionLabel(option)
      : resolvedGetOptionLabel;

  // A shift+click range spans the rows the user sees, never the hidden ones
  const visibleOptions = useMemo(() => {
    if (selectAllEnabled) {
      return selectAllState.displayedOptions.filter(
        (option) => !selectAllState.isMarker(option)
      );
    }
    if (inputValue === "") {
      return [...options];
    }
    return (filterOptions ?? createFilterOptions<T>())([...options], {
      inputValue,
      getOptionLabel: resolvedGetOptionLabel as unknown as (
        option: T
      ) => string,
    });
  }, [
    filterOptions,
    inputValue,
    options,
    resolvedGetOptionLabel,
    selectAllEnabled,
    selectAllState,
  ]);

  const withShiftRange = useShiftRangeSelect<T>({
    options: visibleOptions,
    value: selectedValues,
    onChange: (next, event) =>
      onChange?.(
        event,
        next as Parameters<NonNullable<typeof onChange>>[1],
        "selectOption"
      ),
    isEqual: (a, b) =>
      isOptionEqualToValue ? isOptionEqualToValue(a, b) : a === b,
    isSelectable: (option) =>
      !selectAllState.isMarker(option) &&
      !(getOptionDisabled?.(option) ?? false) &&
      (rangeSelectable?.(option) ?? true),
    maxSelected,
  });

  const baseRenderOption = renderOption ?? renderOptions;
  const defaultRenderOption: NonNullable<typeof baseRenderOption> = (
    props,
    option
  ) => {
    const { key, ...liProps } = props;
    return (
      <li key={key} {...liProps}>
        {effectiveGetOptionLabel(option)}
      </li>
    );
  };
  // The consumer's `renderOption` never sees the "select all" rows
  const finalRenderOption: NonNullable<typeof baseRenderOption> = (
    props,
    option,
    state,
    ownerState
  ) => {
    if (selectAllEnabled && selectAllState.isMarker(option)) {
      const rowState: SelectAllRowState = {
        label: selectAllState.markerLabel(option) ?? "",
        selected: state.selected,
        indeterminate: selectAllState.isPartiallySelected(option),
      };
      if (renderSelectAllOption) {
        return renderSelectAllOption(props, rowState);
      }
      const { key, ...liProps } = props;
      return (
        <li key={key} {...liProps}>
          <Checkbox
            size="small"
            sx={{ mr: 1, p: 0 }}
            checked={rowState.selected}
            indeterminate={rowState.indeterminate}
          />
          {rowState.label}
        </li>
      );
    }
    return (baseRenderOption ?? defaultRenderOption)(
      shiftRangeEnabled ? withShiftRange(props, option) : props,
      option,
      state,
      ownerState
    );
  };

  // The consumer's `renderValue` gets the real selection only; delete
  // indexes are mapped back to the Autocomplete value, which holds the markers
  const finalRenderValue: typeof renderValue =
    multiple && renderValue && selectAllEnabled
      ? (tags, getItemProps, ownerState) => {
          const realIndexes = (tags as T[])
            .map((tag, index) => (selectAllState.isMarker(tag) ? -1 : index))
            .filter((index) => index !== -1);
          const realTags = realIndexes.map((index) => (tags as T[])[index]);
          // Only reached in multiple mode, where MUI's getter takes an index
          const getItemPropsByIndex =
            getItemProps as unknown as AutocompleteRenderValueGetItemProps<true>;
          const getRealItemProps = ((params: { index: number }) =>
            getItemPropsByIndex({
              index: realIndexes[params.index] ?? params.index,
            })) as unknown as typeof getItemProps;
          return renderValue(
            realTags as typeof tags,
            getRealItemProps,
            ownerState
          );
        }
      : renderValue;

  const rootClassName = [
    formStyle ? inputStyles.classes.select : "",
    classes ?? "",
    className ?? "",
  ]
    .filter(Boolean)
    .join(" ");

  const defaultRenderInput: MuiAutocompleteProps<
    T,
    Multiple,
    DisableClearable,
    FreeSolo
  >["renderInput"] = (params) => (
    <TextField
      {...params}
      variant={variant}
      label={label}
      error={error}
      helperText={helperText}
      placeholder={placeholder}
      required={required}
      sx={textFieldSx}
      slotProps={{
        inputLabel: {
          ...params.slotProps.inputLabel,
          ...textFieldSlotProps?.inputLabel,
        },
        htmlInput: {
          ...params.slotProps.htmlInput,
          ...textFieldSlotProps?.htmlInput,
        },
        formHelperText: textFieldSlotProps?.formHelperText,
        input: {
          ...params.slotProps.input,
          ...textFieldSlotProps?.input,
          // No fragment when there is nothing to show: MUI reads any
          // `startAdornment` as "adorned" and floats the label of an empty field
          startAdornment:
            startAdornment ||
            textFieldSlotProps?.input?.startAdornment ||
            params.slotProps.input.startAdornment ? (
              <>
                {startAdornment}
                {textFieldSlotProps?.input?.startAdornment}
                {params.slotProps.input.startAdornment}
              </>
            ) : undefined,
          endAdornment: (
            <>
              {loading && <CircularProgress color="inherit" size={20} />}
              {endAdornment}
              {params.slotProps.input.endAdornment}
            </>
          ),
        },
      }}
    />
  );

  const selectAllProps: Pick<
    MuiAutocompleteProps<T, Multiple, DisableClearable, FreeSolo>,
    | "options"
    | "value"
    | "inputValue"
    | "filterOptions"
    | "getOptionLabel"
    | "isOptionEqualToValue"
    | "getOptionDisabled"
    | "groupBy"
    | "onChange"
    | "onInputChange"
  > = selectAllEnabled
    ? {
        options: selectAllState.displayedOptions,
        value: selectAllState.autocompleteValue as typeof value,
        inputValue,
        // Options are already filtered by the hook
        filterOptions: (displayed) => displayed,
        getOptionLabel: effectiveGetOptionLabel,
        isOptionEqualToValue: (option, other) =>
          selectAllState.isMarker(option) || selectAllState.isMarker(other as T)
            ? option === other
            : isOptionEqualToValue
            ? isOptionEqualToValue(option, other)
            : option === other,
        getOptionDisabled: getOptionDisabled
          ? (option) =>
              selectAllState.isMarker(option)
                ? false
                : getOptionDisabled(option)
          : undefined,
        groupBy: groupBy
          ? (option) => (selectAllState.isMarker(option) ? "" : groupBy(option))
          : undefined,
        onChange: (event, newValue, reason, details) => {
          const next = selectAllState.resolveChange(
            Array.isArray(newValue) ? (newValue as T[]) : [],
            reason,
            details as AutocompleteChangeDetails<T> | undefined
          );
          onChange?.(event, next as typeof newValue, reason, details);
        },
        onInputChange: (event, newInputValue, reason) => {
          if (
            inputValueProp === undefined &&
            reason !== "selectOption" &&
            reason !== "removeOption" &&
            reason !== "reset"
          ) {
            setInnerInputValue(newInputValue);
          }
          onInputChange?.(event, newInputValue, reason);
        },
      }
    : {
        options,
        value,
        inputValue: inputValueProp,
        filterOptions,
        getOptionLabel: resolvedGetOptionLabel,
        isOptionEqualToValue,
        getOptionDisabled,
        groupBy,
        onChange,
        // MUI keeps the text itself here; it is only mirrored for `visibleOptions`
        onInputChange: (event, newInputValue, reason) => {
          if (inputValueProp === undefined) {
            setInnerInputValue(newInputValue);
          }
          onInputChange?.(event, newInputValue, reason);
        },
      };

  return (
    <Autocomplete
      {...autocompleteProps}
      {...selectAllProps}
      className={rootClassName}
      defaultValue={defaultValue ?? undefined}
      getOptionKey={getOptionKey}
      // The built-in ChipTag caps the chips itself, even while focused, so MUI
      // must not truncate again; a custom `renderValue` gets MUI's own `limitTags`.
      limitTags={multiple && !renderValue ? -1 : limitTags}
      loading={loading}
      multiple={multiple}
      ref={rootRef}
      renderInput={renderInput ?? defaultRenderInput}
      renderOption={finalRenderOption}
      renderValue={
        multiple && finalRenderValue
          ? finalRenderValue
          : multiple
          ? (tags, getItemProps) => (
              <ChipTag
                getTagProps={getItemProps}
                labelKey={tagsLabelKey}
                maxVisibleTags={limitTags < 0 ? undefined : limitTags}
                selectAllOptionId={selectAllOptionId}
                translateLabel={translateTags}
                value={tags as GenericObject[]}
              />
            )
          : undefined
      }
      size={size}
      slotProps={{
        ...slotProps,
        listbox: {
          className: inputStyles.classes.scrollbar,
          ...(slotProps?.listbox as Record<string, unknown> | undefined),
        },
      }}
    />
  );
};
